import React, { useState, useMemo, useEffect } from 'react';
import './IssueRebate.css';
import { buildApiUrl, API_ENDPOINTS } from '../config/api';
import { preventScrollChange } from '../utils/inputUtils';

const IssueRebate = ({ user, token }) => {
  // Memoized placeholder values to prevent recalculation during re-renders
  const placeholders = useMemo(() => ({
    agreementNumber: "Enter agreement number or customer name",
    rebateAmount: "Enter rebate amount",
    rebatePerQuintal: "Enter rebate per quintal",
    rebateReason: "Enter reason for rebate (optional)"
  }), []);

  const [searchNumber, setSearchNumber] = useState('');
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Rebate form state
  const [rebateForm, setRebateForm] = useState({
    rebateType: 'amount', // 'amount' or 'rate'
    rebateAmount: '',
    rebatePerQuintalRate: '',
    rebateReason: ''
  });
  
  // Calculation preview
  const [calculationPreview, setCalculationPreview] = useState(null);
  const suggestionQuery = searchNumber.trim();
  const canFindAgreementByNumber = /^\d+$/.test(suggestionQuery);
  const canSuggestAgreements = canFindAgreementByNumber || /^[a-z0-9]{3}/i.test(suggestionQuery);

  useEffect(() => {
    if (!canSuggestAgreements) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      setSuggestionsError('');
      setHighlightedSuggestionIndex(-1);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setSuggestionsLoading(true);
      setSuggestionsError('');

      try {
        const response = await fetch(
          `${buildApiUrl(API_ENDPOINTS.AGREEMENTS)}/suggestions?query=${encodeURIComponent(suggestionQuery)}`,
          {
            headers: {
          'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          }
        );
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to load agreement suggestions');
        }

        setSuggestions(result.data || []);
        setHighlightedSuggestionIndex(-1);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Agreement suggestions error:', err);
          setSuggestions([]);
          setSuggestionsError(err.message || 'Failed to load agreement suggestions');
        }
      } finally {
        if (!controller.signal.aborted) {
          setSuggestionsLoading(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [canSuggestAgreements, suggestionQuery, token]);

  const handleSearch = async (agreementNumber = searchNumber.trim()) => {
    if (!agreementNumber) {
      setError('Please enter an agreement number or select a customer from the suggestions');
      return;
    }

    if (!/^\d+$/.test(agreementNumber)) {
      setError('Find is available for numeric agreement numbers only. Select an agreement from the suggestions.');
      return;
    }

    setLoading(true);
    setError('');
    setAgreement(null);
    setCalculationPreview(null);

    try {
      // Search for agreement by agreement number using history endpoint
      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.AGREEMENTS_HISTORY)}?agreementNumber=${encodeURIComponent(agreementNumber)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Agreement not found');
      }

      if (!result.data || result.data.length === 0) {
        throw new Error('No agreement found with this number');
      }

      const foundAgreement = result.data[0];
      
      // Get rebate-specific agreement details
      const rebateResponse = await fetch(`${buildApiUrl(API_ENDPOINTS.REBATES_AGREEMENT)}/${foundAgreement._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const rebateResult = await rebateResponse.json();

      if (!rebateResponse.ok) {
        throw new Error(rebateResult.error || 'Failed to load agreement details');
      }

      setAgreement(rebateResult.data);
      
      // Reset form
      setRebateForm({
        rebateType: 'amount',
        rebateAmount: '',
        rebatePerQuintalRate: '',
        rebateReason: ''
      });

    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectAgreementSuggestion = (suggestion) => {
    setSearchNumber(suggestion.agreementId);
    setSuggestionsOpen(false);
    setSuggestions([]);
    handleSearch(suggestion.agreementId);
  };

  const handleAgreementSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      setSuggestionsOpen(false);
      return;
    }

    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setSuggestionsOpen(true);
      setHighlightedSuggestionIndex(index => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setSuggestionsOpen(true);
      setHighlightedSuggestionIndex(index => (
        index <= 0 ? suggestions.length - 1 : index - 1
      ));
    } else if (event.key === 'Enter') {
      if (suggestionsOpen && highlightedSuggestionIndex >= 0) {
        event.preventDefault();
        selectAgreementSuggestion(suggestions[highlightedSuggestionIndex]);
      } else if (canFindAgreementByNumber) {
        event.preventDefault();
        handleSearch();
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRebateForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear preview when form changes
    setCalculationPreview(null);
  };

  const handlePreviewCalculation = async () => {
    if (!agreement) return;
    
    const { rebateType, rebateAmount, rebatePerQuintalRate } = rebateForm;
    
    // Validation
    if (rebateType === 'amount' && (!rebateAmount || parseFloat(rebateAmount) <= 0)) {
      setError('Please enter a valid rebate amount');
      return;
    }
    
    if (rebateType === 'rate' && (!rebatePerQuintalRate || parseFloat(rebatePerQuintalRate) <= 0)) {
      setError('Please enter a valid rebate per quintal rate');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.REBATES_PREVIEW), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agreementId: agreement.agreement._id,
          rebateAmount: rebateType === 'amount' ? parseFloat(rebateAmount) : null,
          rebatePerQuintalRate: rebateType === 'rate' ? parseFloat(rebatePerQuintalRate) : null,
          rebateType
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to calculate rebate');
      }

      setCalculationPreview(result.data.calculation);
    } catch (err) {
      console.error('Preview calculation error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleIssueRebate = async () => {
    if (!agreement || !calculationPreview) {
      setError('Please preview the calculation first');
      return;
    }

    if (!window.confirm('Are you sure you want to issue this rebate? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.REBATES_ISSUE), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agreementId: agreement.agreement._id,
          rebateAmount: rebateForm.rebateType === 'amount' ? parseFloat(rebateForm.rebateAmount) : null,
          rebatePerQuintalRate: rebateForm.rebateType === 'rate' ? parseFloat(rebateForm.rebatePerQuintalRate) : null,
          rebateType: rebateForm.rebateType,
          rebateReason: rebateForm.rebateReason
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to issue rebate');
      }

      setSuccess(`Rebate issued successfully! Net payable amount: ₹${calculationPreview.netPayableAfterRebate.toLocaleString('en-IN')}`);
      
      // Reset form and clear agreement
      setAgreement(null);
      setCalculationPreview(null);
      setSearchNumber('');
      setRebateForm({
        rebateType: 'amount',
        rebateAmount: '',
        rebatePerQuintalRate: '',
        rebateReason: ''
      });
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Issue rebate error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate current payable amount
  const currentPayableAmount = agreement ? agreement.currentPayableAmount : 0;
  
  // Check if rebate can be issued
  const canIssueRebate = agreement && !agreement.hasActiveRebate && calculationPreview && 
                        calculationPreview.netPayableAfterRebate >= 0;

  return (
    <div className="issue-rebate">
      <div className="page-header">
        <h2>💰 Issue Rebate</h2>
        <p>Apply financial rebate (discount) on existing agreement's storage charges</p>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <h3>Search Agreement</h3>
        <div className="search-form">
          <div className="form-group">
            <label htmlFor="agreement-search">Agreement Number or Customer Name *</label>
            <input
              id="agreement-search"
              type="text"
              value={searchNumber}
              onChange={(e) => {
                setSearchNumber(e.target.value);
                setAgreement(null);
                setCalculationPreview(null);
                setError('');
                setSuggestionsOpen(true);
              }}
              onFocus={() => setSuggestionsOpen(true)}
              onBlur={() => setSuggestionsOpen(false)}
              onKeyDown={handleAgreementSearchKeyDown}
              placeholder={placeholders.agreementNumber}
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={suggestionsOpen && canSuggestAgreements}
              aria-controls="agreement-suggestions"
            />
            {suggestionsOpen && canSuggestAgreements && (
              <div className="agreement-suggestions" id="agreement-suggestions" role="listbox">
                {suggestionsLoading && (
                  <div className="agreement-suggestion-status">Searching agreements...</div>
                )}
                {!suggestionsLoading && suggestionsError && (
                  <div className="agreement-suggestion-status error">{suggestionsError}</div>
                )}
                {!suggestionsLoading && !suggestionsError && suggestions.length === 0 && (
                  <div className="agreement-suggestion-status">No matching agreements found</div>
                )}
                {!suggestionsLoading && suggestions.map((suggestion, index) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === highlightedSuggestionIndex}
                    key={suggestion._id}
                    className={`agreement-suggestion${index === highlightedSuggestionIndex ? ' highlighted' : ''}`}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => selectAgreementSuggestion(suggestion)}
                  >
                    <span><strong>Agreement:</strong> {suggestion.agreementId}</span>
                    <span><strong>Customer:</strong> {suggestion.customerName || '--'}</span>
                    <span><strong>Father's Name:</strong> {suggestion.fatherName || '--'}</span>
                    <span><strong>Mobile:</strong> {suggestion.mobile || '--'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button 
            onClick={handleSearch} 
            className="btn-find"
            disabled={loading || !canFindAgreementByNumber}
          >
            {loading ? 'Searching...' : 'Find'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Agreement Information (Read-only) */}
      {agreement && (
        <>
          <div className="agreement-info-section">
            <h3>Agreement Information</h3>
            {agreement.hasActiveRebate && (
              <div className="warning-message">
                ⚠️ This agreement already has an active rebate. Cancel the existing rebate first to issue a new one.
              </div>
            )}
            <div className="info-grid">
              <div className="info-item">
                <label>Agreement Number:</label>
                <span>{agreement.agreement.agreementId}</span>
              </div>
              <div className="info-item">
                <label>Lot Number:</label>
                <span>{agreement.agreement.lotNumber}</span>
              </div>
              <div className="info-item">
                <label>Name:</label>
                <span>{agreement.agreement.customerName || '-'}</span>
              </div>
              <div className="info-item">
                <label>Father's Name:</label>
                <span>{agreement.agreement.fatherName || '-'}</span>
              </div>
              <div className="info-item">
                <label>Mobile:</label>
                <span>{agreement.agreement.mobile || '-'}</span>
              </div>
              <div className="info-item">
                <label>Address:</label>
                <span>{agreement.agreement.addressLine1 || '-'}</span>
              </div>
              <div className="info-item">
                <label>Weight (Quintals):</label>
                <span>{agreement.agreement.weightInQuintal}</span>
              </div>
              <div className="info-item">
                <label>Number of Bags:</label>
                <span>{agreement.agreement.numberOfBags}</span>
              </div>
            </div>
            
            <div className="charges-summary">
              <h4>Current Charges</h4>
              <div className="charges-grid">
                <div className="charge-item">
                  <label>Advance Paid:</label>
                  <span>₹{(agreement.agreement.charges?.advanceStorageChargesPaid || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="charge-item">
                  <label>Total Charge:</label>
                  <span>₹{(agreement.agreement.charges?.totalStorageCharge || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="charge-item">
                  <label>Handling Charge:</label>
                  <span>₹{(agreement.agreement.charges?.handlingChargeDue || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="charge-item">
                  <label>Bag Charges:</label>
                  <span>₹{((agreement.agreement.charges?.bagChargeTotal || 0) - (agreement.agreement.charges?.bagChargePaid || 0)).toLocaleString('en-IN')}</span>
                </div>
                <div className="charge-item highlight">
                  <label>Current Payable Amount:</label>
                  <span>₹{currentPayableAmount.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Rebate Input Section */}
          {!agreement.hasActiveRebate && (
            <div className="rebate-section">
              <h3>Rebate Input</h3>
              
              <div className="rebate-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Current Payable Amount</label>
                    <input
                      type="text"
                      value={`₹${currentPayableAmount.toLocaleString('en-IN')}`}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Rebate Type *</label>
                    <select
                      name="rebateType"
                      value={rebateForm.rebateType}
                      onChange={handleInputChange}
                    >
                      <option value="amount">Fixed Amount</option>
                      <option value="rate">Per Quintal Rate</option>
                    </select>
                  </div>
                </div>
                
                {rebateForm.rebateType === 'amount' ? (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Enter Rebate Amount *</label>
                      <input
                        type="number"
                        name="rebateAmount"
                        value={rebateForm.rebateAmount}
                        onChange={handleInputChange}
                        onWheel={preventScrollChange}
                        placeholder={placeholders.rebateAmount}
                        min="0"
                        max={currentPayableAmount}
                        step="0.01"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Enter Rebated Per Quintal Rate *</label>
                      <input
                        type="number"
                        name="rebatePerQuintalRate"
                        value={rebateForm.rebatePerQuintalRate}
                        onChange={handleInputChange}
                        onWheel={preventScrollChange}
                        placeholder={placeholders.rebatePerQuintal}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                )}
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Rebate Reason</label>
                    <textarea
                      name="rebateReason"
                      value={rebateForm.rebateReason}
                      onChange={handleInputChange}
                      placeholder={placeholders.rebateReason}
                      rows="3"
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button
                    onClick={handlePreviewCalculation}
                    className="btn-preview"
                    disabled={loading}
                  >
                    Preview Calculation
                  </button>
                </div>
              </div>
              
              {/* Calculation Preview */}
              {calculationPreview && (
                <div className="calculation-preview">
                  <h4>Rebate Calculation Preview</h4>
                  {/* Updated: Fixed rebate amount display - no negative sign */}
                  <div className="calculation-grid">
                    <div className="calc-item">
                      <label>Original Payable Amount:</label>
                      <span>₹{calculationPreview.originalPayableAmount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="calc-item highlight">
                      <label>Rebate Amount:</label>
                      <span style={{color: 'red', fontWeight: 'bold'}}>
                        -₹{calculationPreview.rebateAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="calc-item">
                      <label>Rebated Storage Charge:</label>
                      <span>₹{calculationPreview.rebatedStorageCharge.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="calc-item">
                      <label>Handling Charge:</label>
                      <span>₹{calculationPreview.handlingCharge.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="calc-item">
                      <label>Bag Charges:</label>
                      <span>₹{calculationPreview.bagCharges.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="calc-item final">
                      <label>Net Payable After Rebate:</label>
                      <span>₹{calculationPreview.netPayableAfterRebate.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button
                      onClick={handleIssueRebate}
                      className="btn-issue-rebate"
                      disabled={loading || !canIssueRebate}
                    >
                      {loading ? 'Processing...' : 'Issue Rebate'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!agreement && !loading && (
        <div className="placeholder-message">
          <p>👆 Enter an agreement number or customer name above to begin rebate process</p>
        </div>
      )}
    </div>
  );
};

export default IssueRebate;